import React from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';

import { Colors } from '../../themes';
import { LabelSearchInput } from '../filter/labelSearchInput';

type Item = {
  id: string;
  name: string;
  isActive?: boolean;
};

type MultiItemSelectorModalProps = {
  showOptions?: boolean;
  search?: string;
  setSearch?: (value: string) => void;
  searchPlaceholder?: string;
  filteredItems?: Item[];
  onItemToggle?: (item: Item) => void;
  onToggleAll?: (selectAll: boolean) => void;
  canCreateItem?: boolean;
  onCreateItem?: (name: string) => Promise<Item>;
  onCreatePress?: () => void;
  isCreating?: boolean;
  createButtonText?: string;
  title?: string;
};

export const MultiItemSelectorModal: React.FC<MultiItemSelectorModalProps> = ({
  showOptions = true,
  search = '',
  setSearch,
  searchPlaceholder = '',
  filteredItems = [],
  onItemToggle,
  canCreateItem,
  onCreateItem,
  onCreatePress,
  isCreating = false,
  createButtonText = '',
  title,
  onToggleAll,
}) => {
  const computedCanCreateItem =
    canCreateItem ??
    (!!search.trim() &&
      !filteredItems.some(
        (item) => item.name.toLowerCase() === search.trim().toLowerCase()
      ));

  const allSelected =
    filteredItems.length > 0 && filteredItems.every((item) => item.isActive);

  if (!showOptions) {
    return null;
  }

  return (
    <View style={styles.optionsListMainContainer}>
      {!!title && <Text style={styles.title}>{title}</Text>}

      <LabelSearchInput
        value={search}
        onChangeText={setSearch || (() => undefined)}
        placeholder={searchPlaceholder}
      />

      <View style={styles.optionsListContainer}>
        <ScrollView
          style={styles.itemsScrollView}
          contentContainerStyle={styles.itemsGrid}
          nestedScrollEnabled
        >
          {!search && (
            <TouchableOpacity
              style={[styles.itemPill, allSelected && styles.itemPillActive]}
              onPress={() => onToggleAll?.(!allSelected)}
            >
              <Text
                style={[
                  styles.itemPillText,
                  allSelected && styles.itemPillTextActive,
                ]}
              >
                All
              </Text>
            </TouchableOpacity>
          )}
          {filteredItems.map((item) => (
            <TouchableOpacity
              key={item.id}
              style={[styles.itemPill, item.isActive && styles.itemPillActive]}
              onPress={() => onItemToggle?.(item)}
            >
              <Text
                style={[
                  styles.itemPillText,
                  item.isActive && styles.itemPillTextActive,
                ]}
              >
                {item.name}
              </Text>
            </TouchableOpacity>
          ))}

          {computedCanCreateItem && onCreateItem && (
            <TouchableOpacity
              style={styles.itemPill}
              onPress={onCreatePress || (() => undefined)}
              disabled={isCreating}
            >
              <Text style={styles.createItemButtonLabel}>
                {createButtonText}:{' '}
              </Text>
              <Text style={styles.createItemButtonText}>{search.trim()}</Text>
            </TouchableOpacity>
          )}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  optionsListMainContainer: {
    flex: 1,
    marginTop: 8,
    borderRadius: 4,
    backgroundColor: Colors.white,
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 16,
  },
  title: {
    fontSize: 14,
    lineHeight: 24,
    fontWeight: '600',
    color: Colors.black,
  },
  optionsListContainer: {
    flex: 1,
    minHeight: 0,
    marginBottom: 0,
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  itemsScrollView: {
    flex: 1,
  },
  itemsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  itemPill: {
    backgroundColor: Colors.primaryGrayBackground,
    height: 40,
    paddingHorizontal: 16,
    borderRadius: 20,
    alignSelf: 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    flexShrink: 0,
  },
  itemPillActive: {
    backgroundColor: Colors.black,
  },
  itemPillText: {
    fontSize: 14,
    lineHeight: 18,
    color: Colors.black,
    fontWeight: '400',
  },
  itemPillTextActive: {
    color: Colors.white,
  },
  createItemButton: {
    backgroundColor: Colors.primaryGrayBackground,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  createItemButtonLabel: {
    fontWeight: '600',
  },
  createItemButtonText: {
    fontSize: 14,
    lineHeight: 16,
    color: Colors.black,
    fontWeight: '400',
  },
});
